/**
 * @typedef {Object} SelectedOption
 * @property {MadeToOrderOption} option
 * @property {number} quantity
 */

/**
 * @typedef {Object} MTOImage
 * @property {string} id
 * @property {string} baseUrl
 * @property {string} shopifyUrl
 * @property {number} width
 * @property {number} height
 */

/**
 * @typedef {Object} MTOPrice
 * @property {string} id
 * @property {string} priceableId
 * @property {string} priceableType
 * @property {number} amount

 * @property {string | null} configurationId
 * @property {string | null} dependentProductGroupId
 * @property {string | null} dependentProductGroupItemId
 */

/**
 * @typedef {Object} ProductGroupItem
 * @property {string} id
 * @property {string} label
 * @property {number} order
 * @property {string} productGroupId
 * @property {string} productId
 * @property {string} variantId
 * @property {MTOPrice[]} prices

 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {string} deletedAt
 */

/**
 * @namespace MadeToOrderEvents
 */

/**
 * Event fired when a product option is selected
 * @event MadeToOrderEvents.OptionSelected
 * @type {CustomEvent}
 * @property {Object} detail - Event details
 * @property {MadeToOrderOption | null} detail.selectedOption - The product group containing the selected option
 */

class ProductGroupSelectedOptions {
  productGroup

  /**
   * @type {SelectedOption[]}
   */
  selected = []

  /**
   * @param {ProductGroup} productGroup
   * @param {SelectedOption[]} initialOptions
   */
  constructor (productGroup, initialOptions = []) {
    this.productGroup = productGroup
    this.selected = initialOptions

    return new Proxy(this, {
      get (target, prop) {
        if (typeof prop === 'symbol') {
          return target[prop]
        }

        if (!isNaN(prop) && prop !== Symbol.iterator) {
          return target.selected[prop]
        }

        return target[prop]
      },
    })
  }

  /**
   * Makes the class iterable
   */
  [Symbol.iterator] () {
    return this.selected[Symbol.iterator]()
  }

  /**
   * Determines whether the specified callback function returns true for any element of an array.
   * @param predicate A function that accepts up to three arguments. The some method calls
   * the predicate function for each element in the array until the predicate returns a value
   * which is coercible to the Boolean value true, or until the end of the array.
   *
   * @returns {boolean}
   */
  some (predicate) {
    return this.selected.some(predicate)
  }

  /**
   * Calls the specified callback function for all the elements in an array. The return value of the callback function is the accumulated result, and is provided as an argument in the next call to the callback function.
   * @param callbackFn A function that accepts up to four arguments. The reduce method calls the callbackfn function one time for each element in the array.
   * @param initialValue If initialValue is specified, it is used as the initial value to start the accumulation. The first call to the callbackFn function provides this value as an argument instead of an array value.
   */
  reduce (callbackFn, initialValue) {
    return this.selected.reduce(callbackFn, initialValue)
  }

  /**
   * @param {MadeToOrderOption} madeToOrderOption
   */
  isCurrentlySelected (madeToOrderOption) {
    return this.selected.some(
      (selectedOption) => selectedOption.option === madeToOrderOption,
    )
  }

  /**
   * Returns the value of the first element in the array where predicate is true, and undefined
   * otherwise.
   * @param predicate find calls predicate once for each element of the array, in ascending
   * order, until it finds one where predicate returns true. If such an element is found, find
   * immediately returns that element value. Otherwise, find returns undefined.
   */
  find (predicate) {
    return this.selected.find(predicate)
  }

  /**
   * Calls a defined callback function on each element of an array, and returns an array that contains the results.
   * @param callbackFn A function that accepts up to three arguments. The map method calls the callbackfn function one time for each element in the array.
   */
  map (callbackFn) {
    return this.selected.map(callbackFn)
  }

  /**
   * @param {MadeToOrderOption} madeToOrderOption
   */
  add (madeToOrderOption) {
    if (this.selected.length >= this.productGroup.maximumNumberOfChoices) {
      this.selected.shift()
    }

    this.selected.push({ option: madeToOrderOption, quantity: 1 })

    return this
  }

  /**
   * @param {MadeToOrderOption} madeToOrderOption
   */
  remove (madeToOrderOption) {
    this.selected = this.selected.filter(
      (selectedOption) => selectedOption.option !== madeToOrderOption,
    )

    return this
  }

  /**
   * @param {MadeToOrderOption} madeToOrderOption
   */
  decrement (madeToOrderOption) {
    const selectedOption = this.selected.find(
      (selectedOption) => selectedOption.option === madeToOrderOption,
    )
    if (!selectedOption) {
      console.error(`Unable to find option for ${madeToOrderOption}`)

      return this
    }

    selectedOption.quantity--

    if (selectedOption.quantity <= 0) {
      this.remove(madeToOrderOption)
    }

    return this
  }

  /**
   * @param {MadeToOrderOption} madeToOrderOption
   */
  increment (madeToOrderOption) {
    if (this.count >= this.productGroup.maximumNumberOfChoices) return this

    const selectedOption = this.selected.find(
      (selectedOption) => selectedOption.option === madeToOrderOption,
    )
    if (!selectedOption) {
      this.add(madeToOrderOption)

      return this
    }

    selectedOption.quantity++

    return this
  }

  get count () {
    return this.selected.reduce(
      (length, selectedOption) => length + selectedOption.quantity,
      0,
    )
  }

  get length () {
    return this.selected.length
  }
}

class ProductGroup {
  /**
   * @param {string} key - The unique identifier for this product group
   */
  key = ''

  /**
   * @type {HTMLUListElement}
   */
  el = null

  /**
   * @type {MadeToOrderTrigger}
   */
  trigger = null

  /**
   * @type {MadeToOrderOption[]}
   */
  options = []

  /**
   * @type {ProductGroupSelectedOptions}
   */
  selectedOptions

  /**
   * @type {MadeToOrderOption | null}
   */
  defaultOption = null

  /**
   * @type {Record<string, any>}
   */
  data = {}

  /**
   * @type {Record<string, any>}
   */
  configurationProductGroup = {}

  /**
   * @param {string} key
   * @param {HTMLUListElement} el
   * @param {MadeToOrder} madeToOrder
   */
  constructor (key, el, madeToOrder) {
    this.key = key
    this.el = el
    this.trigger = madeToOrder.els.trigger.elements.find((trigger) =>
      el.contains(trigger),
    )
    if (!this.trigger) {
      throw new Error(
        `Unable to find trigger for product group with key "${key}"`,
      )
    }

    const configurationProductGroupData =
      madeToOrder.data.configurationProductGroups.find(
        (productGroup) => productGroup.productGroupId === key,
      )
    const options = madeToOrder.els.option.elements.filter((option) =>
      el.contains(option),
    )
    const productGroupData = madeToOrder.data.productGroups.find(
      (productGroup) => productGroup.id === key,
    )

    this.options = options
    this.selectedOptions = new ProductGroupSelectedOptions(
      this,
      productGroupData.productGroupItems.length === 1
        ? [{ option: options[0], quantity: 1 }]
        : [],
    )
    this.defaultOption = options.find((option) => {
      return (
        option.id ===
        productGroupData.productGroupItems.find(
          (productGroupItem) =>
            productGroupItem.productId ===
            configurationProductGroupData.defaultVisualProductId,
        ).id
      )
    })
    this.data = productGroupData
    this.configurationProductGroup = configurationProductGroupData
  }

  getCurrentPrice () {
    return this.selectedOptions.reduce((amount, selectedOption) => {
      const selectedOptionAmount =
        selectedOption.option.getPriceDatasetAmount() * selectedOption.quantity

      if (selectedOptionAmount > 0) {
        return amount + selectedOptionAmount
      }

      return amount
    }, 0)
  }

  getCurrentRrpPrice () {
    return this.selectedOptions.reduce((amount, selectedOption) => {
      const selectedOptionAmount =
        selectedOption.option.getRrpPriceDatasetAmount() *
        selectedOption.quantity

      if (selectedOptionAmount > 0) {
        return amount + selectedOptionAmount
      }

      return amount
    }, 0)
  }

  get currentSelectedOptionCount () {
    return this.selectedOptions.count
  }

  get canHaveMultipleSelections () {
    return (
      this.data.maximumNumberOfChoices > 1 &&
      this.maximumNumberOfChoices - this.minimumNumberOfChoices >= 1
    )
  }

  get minimumNumberOfChoices () {
    return this.data.minimumNumberOfChoices
  }

  get maximumNumberOfChoices () {
    return this.data.maximumNumberOfChoices
  }
}

class MadeToOrder extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return [
      'data-app-url',
      'data-currency-iso-code',
      'data-initial-image-key',
      'data-is-b2b',
      'data-shop-domain',
      'data-variant-id',
    ]
  }

  static get requiredEls () {
    return ['data', 'trade-price-template']
  }

  static get requiredExternalEls () {
    return ['product-media', 'product-price']
  }

  /**
   * @type {Map<string, MTOImage>}
   */
  images = new Map()

  /**
   * @type {ProductGroup[]}
   */
  productGroups = []

  connectedCallback () {
    super.connectedCallback()

    this.productGroups = this.#getProductGroups()

    window.setTimeout(() => {
      if (this.productGroups[0].currentSelectedOptionCount > 0) {
        this.productGroups[0].selectedOptions[0].option.handleClick(
          new PointerEvent('click', {
            bubbles: true,
            cancelable: true,
          }),
        )
      }
    }, 100)

    this.setFormState(true)

    this.log(
      'connectedCallback',
      { productGroups: this.productGroups },
      { data: this.data },
      { productForms: this.productForms },
    )

    this.#prepareImages()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this,
      'OptionSelected',
      this.#onOptionSelection.bind(this),
    )
  }

  async #prepareImages () {
    try {
      const images = await this.#getImages(this.initialImageKey)
      this.#updateProductMedia(images)
    } catch (e) {
      console.error({ e })
    }
  }

  /**
   * Handles when an option is selected in a product group
   * @listens CustomEvent#OptionSelected
   * @param {CustomEvent} event - The option selected event
   * @param {Object} event.detail - The event details
   * @param {MadeToOrderOption} event.detail.selectedOption - The option element where the selection was made
   */
  #onOptionSelection (event) {
    this.#getImages(this.generateImageKey()).then((images) =>
      this.#updateProductMedia(images),
    )

    this.#setTriggerStates()

    if (this.isFormComplete()) {
      this.#updateProductForms()
    }

    window.setTimeout(() => {
      for (const productGroup of this.productGroups) {
        if (productGroup.currentSelectedOptionCount === 0) continue

        productGroup.trigger.updateSelectedDisplay()

        for (const option of productGroup.options) {
          const selectedOption = productGroup.selectedOptions.find(
            (selectedOption) => selectedOption.option === option,
          )

          if (selectedOption) {
            const totalOfOtherSelectedOptions =
              productGroup.selectedOptions.reduce((total, selectedOption) => {
                if (selectedOption.option === option) return total

                return total + selectedOption.quantity
              }, 0)
            option.quantityAdjuster?.setAttribute(
              'max',
              productGroup.maximumNumberOfChoices - totalOfOtherSelectedOptions,
            )
          } else {
            option.quantityAdjuster?.setAttribute(
              'max',
              productGroup.maximumNumberOfChoices -
                productGroup.selectedOptions.count,
            )
          }
        }
      }

      this.#updateExternalProductPricing(event.detail.selectedOption)
    }, 10)
  }

  /**
   * @returns {ProductGroup[]}
   */
  #getProductGroups () {
    const keyAttribute = `data-${this.identifier}-item-key`

    return window.BAO.Utils.getElements(`[${keyAttribute}]`, {
      context: this,
    }).map((el) => {
      const key = el.getAttribute(keyAttribute)

      return new ProductGroup(key, el, this)
    })
  }

  #setTriggerStates () {
    for (let i = 0; i < this.productGroups.length; i++) {
      if (i === 0) continue

      const productGroup = this.productGroups[i]
      const previousProductGroup = this.productGroups[i - 1]

      const previousProductGroupHasSelections =
        previousProductGroup.currentSelectedOptionCount > 0 &&
        previousProductGroup.data.minimumNumberOfChoices > 0
      const previousProductGroupDoesntRequireASelection =
        previousProductGroup.data.minimumNumberOfChoices === 0

      if (
        previousProductGroupHasSelections ||
        previousProductGroupDoesntRequireASelection
      ) {
        if (!productGroup.trigger.hasAttribute('data-always-disabled')) {
          productGroup.trigger.removeAttribute('disabled')
        }
      } else {
        productGroup.trigger.disabled = true
      }
    }
  }

  /**
   * @param {string[]} images
   */
  async #updateProductMedia (images) {
    if (images.length === 0) return

    const imageEls = this.externalEls.image.elements
    const imageContainerEls = this.externalEls['image-container'].elements

    for (const image of images) {
      for (const imageEl of imageEls) {
        const attributeName = `data-${this.identifier}-image-attribute`
        const attributeToUpdate = imageEl.getAttribute(attributeName)
        if (!attributeToUpdate) {
          this.log(
            '#updateProductMedia',
            `Unable to update ${imageEl.localName}.${imageEl.className} as it has no "${attributeName}" attribute`,
          )

          continue
        }

        imageEl.setAttribute(attributeToUpdate, image)
        // this.log(
        //   '#updateProductMedia',
        //   `Added ${image} to ${imageEl.localName}.${imageEl.className}`,
        //   { imageEl },
        // )
      }

      for (const imageEl of imageContainerEls) {
        const attributeName = `data-${this.identifier}-image-attribute`
        const attributeToUpdate = imageEl.getAttribute(attributeName)
        if (!attributeToUpdate) {
          this.log(
            '#updateProductMedia',
            `Unable to update ${imageEl.localName}.${imageEl.className} as it has no "${attributeName}" attribute`,
          )

          continue
        }

        const elsToUpdate = attributeToUpdate.split(',')

        for (const elToUpdate of elsToUpdate) {
          const [selector, attribute] = elToUpdate.split('#')

          if (!selector) {
            this.log(
              '#updateProductMedia',
              `Unable to update ${elToUpdate.localName}.${elToUpdate.className} as it has no selector`,
            )

            continue
          }

          if (!attribute) {
            this.log(
              '#updateProductMedia',
              `Unable to update ${elToUpdate.localName}.${elToUpdate.className} as it has no attribute`,
            )

            continue
          }

          const el = imageEl.querySelector(selector)
          if (!el) {
            this.log(
              '#updateProductMedia',
              `Unable to update ${imageEl.localName}.${imageEl.className} as it can't find an element using the selector "${selector}"`,
            )

            continue
          }

          el.setAttribute(attribute, image)

          // this.log(
          //   '#updateProductMedia',
          //   `Added ${image} to ${el.localName}.${el.className}`,
          //   { el },
          // )
        }
      }
    }
  }

  #updateProductForms () {
    this.createHiddenInput(
      `properties[_made_to_order_info]`,
      this.productGroups
        .map((productGroup) => {
          const selectionData =
            productGroup.selectedOptions
              .map((selectedOption) => {
                const prices = selectedOption.option.getPrices()
                const priceIds = selectedOption.option.getPriceIds()
                const priceIdsString = priceIds
                  .map((priceId) => {
                    if (priceId.startsWith('shopify(')) {
                      return `${priceId}~${selectedOption.quantity}`
                    }

                    const price = prices.find((price) => price.id === priceId)
                    if (!price) return ''

                    if (
                      typeof selectedOption.option.dependentProductGroup ===
                      'undefined'
                    ) {
                      return `${price.id}~${selectedOption.quantity}`
                    }

                    if (price.dependentProductGroupItemId == null) {
                      if (priceIds.length === 1) {
                        return `${price.id}~${selectedOption.option.dependentProductGroup.selectedOptions.count}`
                      }

                      return `${price.id}~${selectedOption.quantity}`
                    }

                    const dependentSelectedOption =
                      selectedOption.option.dependentProductGroup.selectedOptions.find(
                        (option) =>
                          option.option.id ===
                          price.dependentProductGroupItemId,
                      )
                    if (!dependentSelectedOption) {
                      return `${price.id}~${selectedOption.option.dependentProductGroup.selectedOptions.count}`
                    }

                    return `${price.id}~${dependentSelectedOption.quantity}`
                  })
                  .join('*')

                return `${selectedOption.option.id}^${selectedOption.quantity}^${productGroup.data.skuStageKey ?? ''}^${priceIdsString}`
              })
              .join(',') || '-'

          return `${productGroup.key}:${selectionData}`
        })
        .join('|'),
    )

    this.createHiddenInput(
      `properties[_made_to_order_title]`,
      this.productGroups[0].selectedOptions[0].option.getTitle(),
    )

    this.createHiddenInput(
      `properties[_made_to_order_bundle_type`,
      this.data.bundleType,
    )

    this.setFormState(false)
  }

  /**
   * Creates a hidden input element
   * @param {string} name
   * @param {string} value
   */
  createHiddenInput (name, value) {
    for (const productForm of this.productForms) {
      let inputElement = window.BAO.Utils.getElement(`[name="${name}"]`, {
        context: productForm,
      })
      if (!inputElement) {
        this.log('Creating new hidden input', name, 'in', productForm.id)

        inputElement = window.BAO.Utils.createElement('input', {
          type: 'hidden',
          name,
        })

        productForm.appendChild(inputElement)
      }

      this.log('Updating hidden input', name, 'in', productForm.id)
      inputElement.value = value
    }
  }

  isFormComplete () {
    return this.productGroups.every((productGroup) => {
      if (productGroup.data.minimumNumberOfChoices === 0) return true

      return (
        productGroup.currentSelectedOptionCount >=
        productGroup.data.minimumNumberOfChoices
      )
    })
  }

  /**
   * @param {MadeToOrderOption} selectedOption
   */
  #updateExternalProductPricing (selectedOption) {
    const onlyFirstProductGroupHasSelectionsOrNoSelection =
      this.productGroups.reduce((isTrue, productGroup, index) => {
        if (index > 0 && productGroup.currentSelectedOptionCount > 0) {
          return false
        }

        return isTrue
      }, true)

    if (onlyFirstProductGroupHasSelectionsOrNoSelection) {
      if (this.productGroups[0].currentSelectedOptionCount > 1) {
        const price = this.productGroups[0].selectedOptions
          .map(
            (selectedOption) =>
              selectedOption.option.getPriceDatasetAmount() *
              selectedOption.quantity,
          )
          .reduce(
            (amount, selectedOptionAmount) => amount + selectedOptionAmount,
            0,
          )
        const rrpPrice = this.productGroups[0].selectedOptions
          .map(
            (selectedOption) =>
              selectedOption.option.getRrpPriceDatasetAmount() *
              selectedOption.quantity,
          )
          .reduce(
            (amount, selectedOptionAmount) => amount + selectedOptionAmount,
            0,
          )

        for (const element of this.externalEls['product-price'].elements) {
          if (this.isB2bCustomer) {
            element.innerHTML = this.tradePriceTemplate
              .replace('{{ price }}', window.BAO.Utils.formatMoney(price))
              .replace(
                '{{ rrp_price }}',
                window.BAO.Utils.formatMoney(rrpPrice),
              )
          } else {
            element.innerHTML = window.BAO.Utils.formatMoney(price)
          }
        }
      } else {
        for (const element of this.externalEls['product-price'].elements) {
          element.innerHTML = selectedOption.els.price.element.innerHTML
        }
      }

      return
    }

    const price = this.productGroups
      .map((productGroup) => productGroup.getCurrentPrice())
      .reduce((price, currentPrice) => price + currentPrice, 0)
    const rrpPrice = this.productGroups
      .map((productGroup) => productGroup.getCurrentRrpPrice())
      .reduce((price, currentPrice) => price + currentPrice, 0)

    for (const element of this.externalEls['product-price'].elements) {
      if (this.isB2bCustomer) {
        element.innerHTML = this.tradePriceTemplate
          .replace('{{ price }}', window.BAO.Utils.formatMoney(price))
          .replace('{{ rrp_price }}', window.BAO.Utils.formatMoney(rrpPrice))
      } else {
        element.innerHTML = window.BAO.Utils.formatMoney(price)
      }
    }
  }

  generateImageKey () {
    const imageKeys = this.productGroups.map((productGroup) => {
      if (productGroup.currentSelectedOptionCount === 0) {
        if (!productGroup.defaultOption) return null
        return productGroup.defaultOption.getImageKey()
      }
      if (productGroup.currentSelectedOptionCount > 1) {
        return productGroup.selectedOptions[
          productGroup.selectedOptions.length - 1
        ].option.getImageKey()
      }

      return productGroup.selectedOptions[0].option.getImageKey()
    })
    if (imageKeys.some((key) => key == null)) return null

    return imageKeys.join('-')
  }

  /**
   * @param {string | null} imageKey
   */
  async #getImages (imageKey) {
    if (imageKey == null) return []

    const imageApiUrl = `${this.appUrl}/api/images/${this.data.id}/${imageKey}`

    try {
      const { imageUrls } = await window.BAO.Utils.fetchJSON(imageApiUrl, {
        method: 'GET',
      })

      Promise.all(
        imageUrls.map(async (imageUrl) => {
          return new Promise((resolve) => {
            const storedImage = this.images.get(imageUrl)

            if (storedImage?.shopifyUrl) {
              return resolve(storedImage.shopifyUrl)
            }

            const image = new Image()

            image.addEventListener('load', (e) => {
              // TODO: Add to theme here?

              const url = new URL(imageUrl)
              url.pathname = `${url.pathname}/metadata`

              for (const key of url.searchParams.keys()) {
                url.searchParams.delete(key)
              }

              this.#getImageMetadata(url).then((shopifyImageUrl) => {
                this.images.set(imageUrl, {
                  shopifyUrl: shopifyImageUrl,
                  id: imageUrl,
                  baseUrl: imageUrl,
                  height: image.naturalHeight,
                  width: image.naturalWidth,
                })

                resolve(
                  shopifyImageUrl ??
                    `${imageUrl}?shop_domain=${this.shopDomain}`,
                )
              })
            })

            image.src = `${imageUrl}?shop_domain=${this.shopDomain}`
          })
        }),
      )

      return imageUrls.map(
        (imageUrl) => `${imageUrl}?shop_domain=${this.shopDomain}`,
      )
    } catch (e) {
      console.error({ e })
    }
  }

  /**
   * @param {URL} url
   */
  async #getImageMetadata (url, attempt = 0) {
    if (attempt > 10) return null

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Accept: `application/json`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      return this.#getImageMetadata(url, attempt + 1)
    }

    const { shopifyUrl } = await response.json()
    if (!shopifyUrl || shopifyUrl === '') {
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return this.#getImageMetadata(url, attempt + 1)
    }

    this.shopifyUrl = shopifyUrl

    return shopifyUrl
  }

  /**
   * @param {boolean} shouldBeDisabled
   */
  setFormState (shouldBeDisabled) {
    for (const productForm of this.productForms) {
      const submitElement = Array.from(productForm.elements).find(
        (element) => element.type === 'submit',
      )

      const fixedBarQuantityElement = Array.from(productForm.elements).find(
        (element) => element.dataset.mtoFixedBarQuantity !== undefined,
      )

      this.log('setFormState', {
        productForm,
        submitElement,
        shouldBeDisabled,
      })

      if (shouldBeDisabled) {
        submitElement?.setAttribute('disabled', shouldBeDisabled.toString())
        fixedBarQuantityElement?.setAttribute(
          'disabled',
          shouldBeDisabled.toString(),
        )
      } else {
        submitElement?.removeAttribute('disabled')
        fixedBarQuantityElement?.removeAttribute('disabled')
      }
    }
  }

  /**
   * @type {string | null}
   */
  get shopifyUrl () {
    return this._shopifyUrl
  }

  set shopifyUrl (val) {
    this._shopifyUrl = val

    if (val) {
      this.createHiddenInput(`properties[_made_to_order_image]`, val)
    } else {
      this.createHiddenInput(`properties[_made_to_order_image]`, '')
    }
  }

  get currencyIsoCode () {
    return this.dataset.currencyIsoCode
  }

  get data () {
    return JSON.parse(this.els.data.element.textContent.trim())
  }

  get initialImageKey () {
    return this.getAttribute('data-initial-image-key')
  }

  get isB2bCustomer () {
    return this.getAttribute('data-is-b2b') === 'true'
  }

  get appUrl () {
    return this.getAttribute('data-app-url')
  }

  /**
   * @returns {HTMLFormElement[]}
   */
  get productForms () {
    const idInputs = window.BAO.Utils.getElements(
      `[name="id"][value="${this.dataset.variantId}"]`,
    )

    return idInputs.map((input) => input.closest('form'))
  }

  get productMedia () {
    return this.externalEls['product-media'].element
  }

  get shopDomain () {
    return this.getAttribute('data-shop-domain')
  }

  get tradePriceTemplate () {
    return this.els['trade-price-template'].element.innerHTML.trim()
  }
}

if (!customElements.get('made-to-order')) {
  customElements.define('made-to-order', MadeToOrder)
}
